export const getTemplateValue = ({
  key,
  answersMap,
  agentData,
  translations,
  language = 'EN',
}) => {
  const predictedHairClass =
    answersMap?.hairPrediction?.predictedHairClass || null

  const correctedHairClass = answersMap?.correctedHairClass || null

  const isPredictionCorrect = String(
    answersMap?.isPredictedHairClassCorrect || '',
  )
    .trim()
    .toLowerCase()

  const finalHairClass =
    isPredictionCorrect === 'no' && correctedHairClass
      ? correctedHairClass
      : predictedHairClass

  const fullName = agentData?.agentFullName || ''
  const firstName = fullName.split(' ')[0] || ''

  const gender = String(
    answersMap?.demographics?.gender || answersMap?.gender || '',
  )
    .trim()
    .toLowerCase()

  const normalizedLanguage = String(language || 'EN')
    .trim()
    .toUpperCase()

  const getLocalizedQuestion = (label, fallback) => {
    const entries = [
      translations?.subscription?.newUserFlow?.questions,
      translations?.subscription?.questions,
      translations?.newUserFlow?.questions,
    ]

    for (const section of entries) {
      const match = section?.find((item) => item.label === label)
      const localized =
        match?.question?.[normalizedLanguage] ||
        match?.question?.EN ||
        match?.[normalizedLanguage] ||
        match?.EN

      if (localized) return localized
    }

    return fallback
  }

  const getProfileValue = (...values) => {
    const translateValue = (value) => {
      const stringValue = String(value || '').trim()

      if (!stringValue) return null

      const canonicalTranslation =
        translations?.menu?.questionnaireValues?.[stringValue]

      if (canonicalTranslation) {
        return (
          canonicalTranslation?.[normalizedLanguage] ||
          canonicalTranslation?.EN ||
          stringValue
        )
      }

      const profileTranslation =
        translations?.menu?.profileValues?.[stringValue]

      if (profileTranslation) {
        return (
          profileTranslation?.[normalizedLanguage] ||
          profileTranslation?.EN ||
          stringValue
        )
      }

      return stringValue
    }

    for (const value of values) {
      if (value == null) continue

      const normalizedValues = (
        Array.isArray(value) ? value : [value]
      )
        .map(translateValue)
        .filter(
          (item) =>
            item &&
            !['null', 'undefined'].includes(
              String(item).toLowerCase(),
            ),
        )

      if (normalizedValues.length) {
        return normalizedValues.join(', ')
      }
    }

    return null
  }

  const getExistingUserFlowText = (path, fallback) => {
    const parts = path.split('.')

    let entry =
      translations?.subscription?.existingUserFlow?.flows ||
      translations?.existingUserFlow?.flows

    for (const part of parts) {
      entry = entry?.[part]
    }

    return (
      entry?.[normalizedLanguage] ||
      entry?.EN ||
      fallback
    )
  }

  const templateValues = {
    predictedHairClass:
      answersMap?.hairPrediction?.predictedHairClass ||
      answersMap?.predictedHairClass ||
      'our best estimate',

    predictedHairDescription:
      answersMap?.hairPrediction?.predictedHairDescription ||
      answersMap?.predictedHairDescription ||
      (normalizedLanguage === 'FR'
        ? 'Cette estimation est basée sur les cheveux actuellement visibles sur la photo téléchargée.'
        : normalizedLanguage === 'NL'
          ? 'Deze schatting is gebaseerd op het haar dat momenteel zichtbaar is op de geüploade foto.'
          : normalizedLanguage === 'SW'
            ? 'Makadirio haya yanategemea nywele zinazoonekana kwa sasa kwenye picha iliyopakiwa.'
            : 'This estimate is based on your currently visible hair in the uploaded photo.'),
          
    predictedHairTypeHint:
      finalHairClass || 'straight, wavy, curly, coily/afro',

    finalHairClass: finalHairClass || 'your hair class',

    firstName,

    firstNamePrefix: fullName ? `, ${firstName}` : '',

    hairGoals:
      getProfileValue(
        answersMap?.hairProfile?.hair_goals,
        answersMap?.hair_goals,
        agentData?.hairProfile?.hair_goals,
        agentData?.hair_goals,
      ) || 'not recorded',

    hairChallenges:
      getProfileValue(
        answersMap?.hairProfile?.hair_challenges,
        answersMap?.hair_challenges,
        agentData?.hairProfile?.hair_challenges,
        agentData?.hair_challenges,
      ) || 'not recorded',

    scalpChallenges:
      getProfileValue(
        answersMap?.hairProfile?.scalp_challenges,
        answersMap?.scalp_challenges,
        agentData?.hairProfile?.scalp_challenges,
        agentData?.scalp_challenges,
      ) || 'not recorded',

    hairClass:
      answersMap?.hairPrediction?.predictedHairClass ||
      answersMap?.predictedHairClass ||
      answersMap?.hair_type ||
      answersMap?.hair_class ||
      agentData?.hair_type ||
      agentData?.hairClass ||
      agentData?.hair_class ||
      'your hair type',

    hairType: finalHairClass || '',

    progressSummary:
      answersMap?.progressSummary ||
      'I have prepared a summary based on your recent activity',

    ecoPreferenceSuffix:
      answersMap?.routinePreferences?.ecoFriendly === true ||
      answersMap?.ecoFriendly === true ||
      answersMap?.wantsEcoFriendlyProducts === true
        ? ' with eco-conscious options included'
        : '',

    profileConfirmQuestion: getExistingUserFlowText(
      'newProduct.confirmQuestion',
      'Does this information still look correct?',
    ),

    updateHairProfileQuestion: getExistingUserFlowText(
      'updateProfile.hairProfileQuestion',
      'Let’s refresh your hair profile. Since your last visit, has anything changed with your current hair situation — for example whether your hair is natural, braided, in locs or chemically treated? Tell me about any hair or scalp concerns you’re experiencing now, and what you’d currently like to achieve with your hair.',
    ),

    updateRoutineQuestion: getExistingUserFlowText(
      'updateProfile.routineQuestion',
      'Now let’s check in on your routine. Are you still following the same hair routine as last time? Tell me what products you’re currently using, how often you use them, and whether your usual monthly budget has changed.',
    ),

    updateMedicalLifestyleQuestion: getExistingUserFlowText(
      'updateProfile.medicalQuestion',
      'And one quick health and lifestyle check-in. Since your last visit, has anything changed with your allergies or scalp sensitivities, medical conditions, medications or treatments, or pregnancy/postpartum information? You can also tell me if lifestyle factors such as stress, diet or climate are affecting your hair differently now.',
    ),

    updateCompleteMessage: getExistingUserFlowText(
      'updateProfile.complete',
      'Thanks — I’ve refreshed your information. Let’s use your updated profile to continue with your hair analysis and recommendations.',
    ),

    analysisRecipientQuestion: getExistingUserFlowText(
      'someoneElse.recipientQuestion',
      'Who are we doing the analysis for?',
    ),

    someoneElseDemographicsQuestion: getExistingUserFlowText(
      'someoneElse.demographics',
      'To help us personalise the analysis, what is their age, gender, and location?',
    ),

    someoneElseHairPhotoQuestion: getExistingUserFlowText(
      'someoneElse.hairPhoto',
      'Please take a photo of their hair or upload one. This helps us analyse their hair texture and condition for more relevant recommendations.',
    ),

    someoneElsePredictionReviewQuestion: getExistingUserFlowText(
      'someoneElse.predictionReview',
      'Based on the hair visible in the photo, we predict their hair class is {predictedHairClass}. {predictedHairDescription}',
    ),

    someoneElsePredictionConfirmQuestion: getExistingUserFlowText(
      'someoneElse.predictionConfirm',
      'Does this predicted hair class look correct for their hair right now?',
    ),

    someoneElseCorrectHairClassQuestion: getExistingUserFlowText(
      'someoneElse.correctHairClass',
      'No problem — please select their correct hair class from the options below.',
    ),

    someoneElseHairProfileQuestion: getExistingUserFlowText(
      'someoneElse.hairProfile',
      'Thanks. We will use {finalHairClass} as their hair class. What best describes their current hair situation? For example: natural, braids, locs or chemically treated hair. Do they have any hair or scalp concerns, and what would they like to achieve with their hair?',
    ),

    someoneElseRoutineQuestion: getExistingUserFlowText(
      'someoneElse.routine',
      'Great. Now tell me about their current hair routine. What products do they use, how often do they use them, and what is their usual monthly budget for hair care?',
    ),

    someoneElseMedicalLifestyleQuestion: getExistingUserFlowText(
      'someoneElse.medicalLifestyle',
      'A few quick things to help us personalise their recommendations — do they have any allergies or scalp sensitivities, medical conditions, medications or treatments we should know about? Are there any lifestyle factors such as stress, diet or climate that affect their hair?',
    ),

    demographicsQuestion:
      answersMap?.shoppingFor === 'Child'
        ? getLocalizedQuestion(
            'demographics',
            "If you're shopping for a child, please provide the child's age and gender, and your location for climate.",
          )
        : getLocalizedQuestion(
            'demographics',
            'What is your age, gender, and location?',
          ),
    medicalContextPhrase: (() => {
      const phraseKey =
        gender === 'male'
          ? 'male'
          : 'other'

      const phrase =
        translations?.chat
          ?.medicalContextPhrase
          ?.[phraseKey]

      return (
        phrase?.[
          normalizedLanguage
        ] ||
        phrase?.EN ||
        (
          gender === 'male'
            ? 'a medical condition we'
            : 'a medical condition or a pregnancy we'
        )
      )
    })(),
  }

  return templateValues[key] ?? ''
}

export const renderQuestionText = ({
  step,
  localizedText,
  answersMap,
  agentData,
  translations,
  language,
}) => {
  const rawText =
    localizedText ||
    step?.questionTemplate ||
    step?.question ||
    step?.fallbackQuestion ||
    ''

  if (!rawText) return ''

  return rawText
    .replace(/\{(.*?)\}/g, (_, key) =>
      getTemplateValue({
        key,
        answersMap,
        agentData,
        translations,
        language,
      }),
    )
    .replace(/\s+/g, ' ')
    .trim()
}

export const getTranslatedOptions = ({
  step,
  localizedAnswers,
  englishAnswers,
}) => {
  if (!step?.options) return step?.options || []
  if (!localizedAnswers) return step.options

  if (step.type === 'picture') {
    return step.options.map((option) => ({
      ...option,
      label: localizedAnswers[option.value] || option.label,
    }))
  }

  if (!Array.isArray(englishAnswers) || !Array.isArray(localizedAnswers)) {
    return step.options
  }

  return step.options.map((option) => {
    const englishValue = typeof option === 'object' ? option.value : option

    const answerIndex = englishAnswers.indexOf(englishValue)

    if (answerIndex === -1) return option

    return {
      value: englishValue,
      label: localizedAnswers[answerIndex],
    }
  })
}

export const cleanRoutineOutput = (text) => {
  if (!text) return ''

  return text
    .replace(/^\s*#{1,6}\s*/gm, '')
    .replace(/\*\*/g, '')
    .trim()
}

export const getWelcomeMessage = ({ isExistingUser, agentFullName }) => {
  const displayName = agentFullName || 'there'

  if (isExistingUser) {
    return `Welcome back, ${displayName} 👋 Great to see you again. Here's a quick look at your hair profile from your last visit.`
  }

  return `Welcome, ${displayName} 🎉 I can help you find the best products for your hair. I'll ask a few short questions so I can personalise your routine.`
}
